import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CircleDollarSign,
  Clock3,
  History,
  Loader2,
  PackageCheck,
  ShoppingBag,
  XCircle,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { qrPrintApi } from '../../api/qrPrintApi';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  formatMoney,
  isSuperAdminUser,
} from '../../utils/qrPrintHelpers';
import '../../styles/QrPrint/QrPrintOrderDetails.css';

const translateFallback = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const formatStatus = (status) => {
  return String(status || '')
    .replace(/_/g, ' ')
    .trim();
};

const getOrderItemsCount = (order) => {
  return order?.items?.length || order?.items_count || 0;
};

const terminalStatuses = [
  'completed',
  'cancelled',
  'rejected',
];

// Only display the fulfillment stages. Quotation and negotiation are legacy
// backend statuses and intentionally have no progress steps in this UI.
const fulfillmentSteps = [
  { status: 'design_confirmed' },
  { status: 'in_production' },
  { status: 'ready' },
  { status: 'shipped' },
  { status: 'completed' },
];

const awaitingDesignStatuses = new Set([
  'quotation_requested',
  'negotiating',
  'price_accepted',
  'pending',
  'awaiting_design',
]);

const getFulfillmentProgress = (status) => {
  const index = fulfillmentSteps.findIndex((step) => step.status === status);
  // A newly submitted order is actively in the design stage, but the
  // design is NOT complete until the backend confirms it.
  const currentIndex = awaitingDesignStatuses.has(status) ? 0 : index;
  return {
    steps: fulfillmentSteps,
    currentIndex,
    completedIndex: index,
  };
};

const displayOrderStatus = (status) =>
  awaitingDesignStatuses.has(status) ? 'Setting the design' : formatStatus(status);

export default function QrPrintOrderDetails() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { orderId } = useParams();

  const [order, setOrder] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [saving, setSaving] = useState(false);

  const admin = isSuperAdminUser(user);

  const load = async () => {
    const response = await qrPrintApi.getOrder(orderId);
    setOrder(response.data?.data || null);
  };

  useEffect(() => {
    load();
  }, [orderId]);

  const progress = useMemo(
    () => getFulfillmentProgress(order?.status),
    [order?.status],
  );

  if (!order) {
    return (
      <div className="qr-print-order-details-loading">
        <Loader2
          size={30}
          className="qr-print-order-details-spin"
        />
      </div>
    );
  }

  const advanceStatus = async (status) => {
    setSaving(true);

    try {
      await qrPrintApi.updateOrderStatus(order.id, {
        status,
        note: statusNote || null,
      });

      setStatusNote('');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    setSaving(true);

    try {
      await qrPrintApi.cancelOrder(order.id);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const nextAdminStatuses = {
    quotation_requested: ['design_confirmed'],
    negotiating: ['design_confirmed'],
    price_accepted: ['design_confirmed'],
    design_confirmed: ['in_production'],
    in_production: ['ready'],
    ready: ['shipped', 'completed'],
    shipped: ['completed'],
  }[order.status] || [];

  const waitingForDesign = awaitingDesignStatuses.has(order.status);
  const isItemDesigned = (item) => {
    const options = item.selected_options || {};
    const config = item.design?.configuration || {};
    return item.design?.status === 'ready' &&
      options.needs_customer_design !== true && config.needs_customer_design !== true &&
      options.design_status !== 'default' && config.design_status !== 'default';
  };
  const allItemsDesigned = Boolean(order.items?.length) && order.items.every(isItemDesigned);

  const orderItemsCount = getOrderItemsCount(order);
  const historyCount =
    (order.status_history || order.statusHistory || []).length;
  const isTerminal = terminalStatuses.includes(order.status);

  return (
    <main className="qr-print-order-details-page">
      <header className="qr-print-order-details-header">
        <div>
          <Link to="/qr-print/orders" className="qr-print-order-details-back">
            <ArrowLeft size={17} />
            {translateFallback(
              t,
              'qrPrint.orderDetails.back',
              'Back to print orders',
            )}
          </Link>
          <span className="qr-print-order-details-eyebrow">
            <PackageCheck size={16} />
            {order.order_number}
          </span>
          <h1>
            {translateFallback(
              t,
              'qrPrint.orderDetails.title',
              'Print order details',
            )}
          </h1>
        </div>

        <span className={`qr-print-status is-${order.status}`}>
          {translateFallback(
            t,
            `qrPrint.statuses.${order.status}`,
            displayOrderStatus(order.status),
          )}
        </span>
      </header>

      <section className="qr-print-order-summary-grid">
        <article>
          <span>
            <ShoppingBag size={18} />
          </span>

          <div>
            <strong>{orderItemsCount}</strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orderDetails.summaryItems',
                'Products',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <CircleDollarSign size={18} />
          </span>

          <div>
            <strong>
              {formatMoney(
                order.final_total ?? order.estimated_total,
                order.currency,
              )}
            </strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orderDetails.summaryTotal',
                'Current total',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <Clock3 size={18} />
          </span>

          <div>
            <strong>
              {isTerminal
                ? translateFallback(
                    t,
                    `qrPrint.statuses.${order.status}`,
                    formatStatus(order.status),
                  )
                : translateFallback(
                    t,
                    'qrPrint.orderDetails.summaryActive',
                    'Active',
                  )}
            </strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orderDetails.summaryState',
                'Order state',
              )}
            </p>
          </div>
        </article>
      </section>

      <section className="qr-print-progress-card">
        {progress.steps.map((step, index) => {
          const done = progress.completedIndex >= 0 && index <= progress.completedIndex;
          const current = index === progress.currentIndex;
          return (
            <div
              className={`qr-print-progress-step ${done ? 'is-done' : ''} ${current ? 'is-current' : ''}`}
              key={step.status}
              aria-current={current ? 'step' : undefined}
            >
              <span style={current && !done ? {
                backgroundColor: '#b45309',
                borderColor: '#b45309',
                color: '#fff',
              } : undefined}>{done ? <Check size={14} /> : index + 1}</span>
              <small style={current ? { color: '#92400e', fontWeight: 800 } : undefined}>{translateFallback(
                  t,
                  step.status === 'design_confirmed'
                    ? 'qrPrint.orderDetails.progressSettingDesign'
                    : `qrPrint.statuses.${step.status}`,
                  step.status === 'design_confirmed'
                    ? 'Setting the design'
                    : formatStatus(step.status),
                )}</small>
            </div>
          );
        })}
      </section>

      <div className="qr-print-order-details-grid">
        <section className="qr-print-order-main">
          <div className="qr-print-form-card">
            <h2>
              {translateFallback(
                t,
                'qrPrint.orderDetails.items',
                'Products',
              )}
            </h2>

            {order.items?.map((item) => (
              <article className="qr-print-order-item" key={item.id}>
                <div>
                  <strong>{item.product?.name}</strong>
                  <p>
                    {item.design?.menu?.name ||
                      translateFallback(
                        t,
                        'qrPrint.orderDetails.menu',
                        'Menu',
                      )}
                  </p>
                </div>
                {waitingForDesign && !isItemDesigned(item) && (
                  <Link
                    to={`/qr-print/products/${item.qr_print_product_id ?? item.product?.id}/customize?${new URLSearchParams({
                      orderId: String(order.id),
                      orderItemId: String(item.id),
                      establishmentId: String(order.establishment_id),
                      menuId: String(item.design?.menu_id ?? item.design?.menu?.id ?? ''),
                    }).toString()}`}
                    className="qr-print-button qr-print-button-primary qr-print-button-small"
                  >{translateFallback(t, 'qrPrint.orderDetails.setProductDesign', 'Set product design')}</Link>
                )}
                {isItemDesigned(item) && <span style={{ color: '#166534' }}>{translateFallback(t, 'qrPrint.orderDetails.designReady', 'Design ready')}</span>}
                <span>x{item.quantity}</span>
                <strong>{formatMoney(item.estimated_total)}</strong>
              </article>
            ))}
          </div>

          <div className="qr-print-form-card">
            <h2>
              {translateFallback(
                t,
                'qrPrint.orderDetails.history',
                'Order history',
              )}
            </h2>

            <div className="qr-print-history">
              {historyCount === 0 ? (
                <div className="qr-print-thread-empty">
                  <History size={20} />
                  <span>
                    {translateFallback(
                      t,
                      'qrPrint.orderDetails.noHistory',
                      'No history yet.',
                    )}
                  </span>
                </div>
              ) : null}

              {(order.status_history || order.statusHistory || []).map(
                (entry) => (
                  <article key={entry.id}>
                    <span />
                    <div>
                      <strong>
                        {translateFallback(
                          t,
                          `qrPrint.statuses.${entry.to_status}`,
                          String(entry.to_status).replace(/_/g, ' '),
                        )}
                      </strong>
                      {entry.note ? <p>{entry.note}</p> : null}
                    </div>
                  </article>
                ),
              )}
            </div>
          </div>
        </section>

        <aside className="qr-print-order-sidebar">
          <div className="qr-print-checkout-card">
            <CircleDollarSign size={24} />
            <h2>
              {translateFallback(
                t,
                'qrPrint.orderDetails.priceSummary',
                'Price summary',
              )}
            </h2>

            <div className="qr-print-cart-total">
              <span>
                {translateFallback(
                  t,
                  'qrPrint.orderDetails.estimate',
                  'Estimate',
                )}
              </span>
              <strong>
                {formatMoney(order.estimated_total, order.currency)}
              </strong>
            </div>

            <div className="qr-print-cart-total is-final">
              <span>
                {translateFallback(
                  t,
                  'qrPrint.orderDetails.finalPrice',
                  'Final price',
                )}
              </span>
              <strong>
                {formatMoney(order.final_total, order.currency)}
              </strong>
            </div>
          </div>

          {admin && nextAdminStatuses.length > 0 ? (
            <div className="qr-print-checkout-card">
              <h2>
                {translateFallback(
                  t,
                  'qrPrint.orderDetails.production',
                  'Production',
                )}
              </h2>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.orderDetails.statusNote',
                    'Status note',
                  )}
                </span>
                <textarea
                  rows="3"
                  value={statusNote}
                  onChange={(event) => setStatusNote(event.target.value)}
                />
              </label>

              {nextAdminStatuses.map((status) => (
                <button
                  type="button"
                  key={status}
                  disabled={saving || (status === 'design_confirmed' && !allItemsDesigned)}
                  className="qr-print-button qr-print-button-primary qr-print-button-full"
                  onClick={() => advanceStatus(status)}
                >
                  {translateFallback(
                    t,
                    `qrPrint.statusActions.${status}`,
                    status === 'design_confirmed' ? 'Confirm design' : `Move to ${status.replace(/_/g, ' ')}`,
                  )}
                </button>
              ))}
            </div>
          ) : null}

          {!admin &&
          !['in_production', 'ready', 'shipped', 'completed', 'cancelled', 'rejected'].includes(
            order.status,
          ) ? (
            <button
              type="button"
              disabled={saving}
              className="qr-print-button qr-print-button-danger qr-print-button-full"
              onClick={cancel}
            >
              <XCircle size={18} />
              {translateFallback(
                t,
                'qrPrint.orderDetails.cancel',
                'Cancel request',
              )}
            </button>
          ) : null}
        </aside>
      </div>
    </main>
  );
}